<template>
  <div
    class="border-gray flex flex-col bg-white"
    :class="{
      '-ml-4 -mr-4 mt-[5px] w-screen border-b px-2 py-4 shadow-md md:rounded-md':
        !removeBoxShadow,
      'w-full p-0': removeBoxShadow,
    }"
  >
    <div class="flex w-full items-center gap-3">
      <div class="relative mb-4 flex w-full flex-col">
        <div class="flex">
          <div class="flex-4 dashboard-origin-location grow-[4]">
            <LocationSearchInput
              type="origin"
              class="flex-4"
              placeholder="Search an Origin"
              :input-value="filters.origin"
              :show-icons="false"
              :show-clear-icon="true"
              :is-slim="false"
              @set-location="setOriginFilters"
              @set-selected-location="setSelectedOrigin"
            ></LocationSearchInput>
          </div>

          <div
            class="dashboard-origin-dropdown relative w-full min-w-[90px] flex-1"
          >
            <dropdown-select
              :options="deadheadOptions"
              :default="selectedOriginDeadhead"
              :value="selectedOriginDeadhead"
              :exclude-selected="true"
              class="deadhead-dropdown !h-12 border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
              @input="handleOriginDeadheadSelected"
            />
          </div>
        </div>

        <div class="flex">
          <div class="flex-4 dashboard-destination-location grow-[4]">
            <LocationSearchInput
              type="destination"
              placeholder="Search a Destination"
              :input-value="filters.destination"
              :show-icons="false"
              :show-clear-icon="true"
              :is-slim="false"
              @set-location="setDestinationFilters"
              @set-selected-location="setSelectedDestination"
            ></LocationSearchInput>
          </div>

          <div
            class="dashboard-destination-dropdown relative w-full min-w-[90px] flex-1"
          >
            <dropdown-select
              :options="deadheadOptions"
              :default="selectedDestinationDeadhead"
              :value="selectedDestinationDeadhead"
              :exclude-selected="true"
              class="deadhead-dropdown !h-12 border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
              @input="handleDestinationDeadheadSelected"
            />
          </div>
        </div>
      </div>

      <div
        class="mb-4 cursor-pointer rounded-full border border-light-gray bg-white p-2 hover:opacity-70"
        @click="toggleLocationInputOrder"
      >
        <img
          class=""
          src="@/assets/arrows-up-and-down.svg"
          alt="arrows icon"
        />
      </div>
    </div>

    <div
      class="equipment-dropdown flex w-full items-center justify-between gap-3"
    >
      <dropdown-select
        :options="equipmentOptions"
        :default="selectedEquipment"
        :value="selectedEquipment"
        :exclude-selected="true"
        class="mt-6 !h-12 flex-1 border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
        @input="handleSelectedEquipment"
      />

      <div class="w-full flex-1">
        <form-field-date
          label=" "
          :hide-label="true"
          class="!mt-0 !h-12 !text-xs"
          placeholder="Pickup Date Range"
          :model-value="pickupPlaceholder"
          name="pickup_date"
          input-type="date"
          :is-slim="true"
          :hidden="false"
          :monospace="false"
          :show-confirm-button="false"
          @update:model-value="dynamicUpdatePickupDate"
          @toggle-date-picker="toggleShowPickupDatePicker"
        />
      </div>
    </div>

    <div class="mt-4 w-full">
      <button-main
        class="flex !h-[48px] items-center justify-center gap-2 !py-3 text-1md !text-dark-gray"
        :disabled="isSearchDisabled"
        @click="handleSearch"
      >
        <img
          class=""
          src="@/assets/magnifying-glass-black.svg"
          alt="magnifying glass"
        />
        Search
      </button-main>
    </div>
    <DisplayEquipmentTableModal
      :visible="isEquipmentModalVisible"
      @close="isEquipmentModalVisible = false"
    />
    <TeleportModalWrapperComponent
      :margin-top="innerHeight < 700 ? 'mt-[60px]' : ''"
      :align="innerHeight < 700 ? 'start' : 'center'"
      :is-modal-open="showPickupDatePicker"
      @close-modal="showPickupDatePicker = false"
    >
      <div class="max-w-[300px] rounded-lg bg-white p-4 shadow-md">
        <date-picker-range
          :start-date="startPickupDate"
          :end-date="endPickupDate"
          :field="'pickup_datepicker'"
          date-format="MM/DD/YYYY"
          :disable-previous="true"
          :restrict-previous="true"
          @day-selected="setSelectedPickupDay"
        />
      </div>
    </TeleportModalWrapperComponent>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import LocationSearchInput from '@/components/loads/LocationSearchInput.vue';
import DropdownSelect from '@/components/loads/LoadDropdown.vue';
import ButtonMain from '@/components/Button.vue';
import FormFieldDate from '@/components/FormFieldDate.vue';
import DatePickerRange from '@/components/DatePickerRange.vue';
import { createFilterQuery } from '@/utils/loadFilters.js';
import DisplayEquipmentTableModal from '@/components/modals/displayEquipmentTableModal.vue';
import { useExchangeAccess } from '@/composables';
import TeleportModalWrapperComponent from '@/components/TeleportModalWrapperComponent.vue';
import { useScreenSize } from '@/composables/useScreenSize';

export default {
  name: 'DashboardLoadSearch',
  components: {
    TeleportModalWrapperComponent,
    ButtonMain,
    FormFieldDate,
    DatePickerRange,
    LocationSearchInput,
    DisplayEquipmentTableModal,
    DropdownSelect,
  },
  props: {
    removeBoxShadow: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      deadheadOptions: [
        '50 mi',
        '100 mi',
        '150 mi',
        '200 mi',
        '250 mi',
        '300 mi',
        '350 mi',
        '400 mi',
        '450 mi',
        '500 mi',
      ],
      equipmentOptions: [
        'All Equipment',
        'Flatbed',
        'Power Only',
        'Reefer',
        'Van',
      ],
    };
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { displayEquipmentModal } = useExchangeAccess();
    const { innerHeight } = useScreenSize();

    const isEquipmentModalVisible = ref(false);

    onMounted(() => {
      isEquipmentModalVisible.value = displayEquipmentModal.value;
    });

    const filters = ref({
      origin: '',
      destination: '',
      selectedOrigin: '',
      selectedDestination: '',
      deadhead: '',
      city_state_origin: '',
      city_state_destination: '',
      origin_deadhead: '150',
      destination_deadhead: '150',
      equipment: [],
    });

    const isSearchDisabled = computed(
      () =>
        !filters.value?.origin?.length && !filters.value?.destination?.length
    );

    const selectedOriginDeadhead = ref('150 mi');
    const selectedDestinationDeadhead = ref('150 mi');
    const handleOriginDeadheadSelected = (item) => {
      console.log('select deadhead', item);
      const origin = item?.split(' ')?.[0];
      filters.value.origin_deadhead = origin;
      selectedOriginDeadhead.value = origin;
    };
    const handleDestinationDeadheadSelected = (item) => {
      console.log('select deadhead', item);
      const destination = item?.split(' ')?.[0];
      filters.value.destination_deadhead = destination;
      selectedDestinationDeadhead.value = destination;
    };

    const setOriginFilters = (origin) => {
      filters.value.origin = origin;
    };

    const setDestinationFilters = (destination) => {
      filters.value.destination = destination;
    };

    const setSelectedOrigin = (origin) => {
      filters.value.origin_area_id = origin?.id;
      filters.value.selectedOrigin = origin?.code;
    };

    const setSelectedDestination = (destination) => {
      filters.value.destination_area_id = destination?.id;
      filters.value.selectedDestination = destination?.code;
    };

    const equipmentMap = {
      Flatbed: 'flatbed',
      'Power Only': 'power_only',
      Reefer: 'reefer',
      Van: 'van',
    };

    const selectedEquipment = ref('All Equipment');
    const handleSelectedEquipment = (item) => {
      selectedEquipment.value = `Equipment: ${item}`;
      console.log('select equipment', item);

      if (item !== 'All Equipment') {
        filters.value.equipment = [equipmentMap[item]];
      }
    };

    const initialDate = computed(() => {
      return dayjs().format('MM/DD/YYYY');
    });

    const selectedPickupDate = ref(filters.value.pickup_start || '');
    const selectedPickupEndDate = ref(filters.value.pickup_end || '');
    const showPickupDatePicker = ref(false);

    const pickupPlaceholder = computed(() => {
      if (filters.value.pickup_start && filters.value.pickup_end) {
        return `${dayjs(filters.value.pickup_start).format('MMM DD, YYYY')} - ${dayjs(filters.value.pickup_end).format('MMM DD, YYYY')}`;
      } else {
        return '';
      }
    });

    const toggleShowPickupDatePicker = () => {
      showPickupDatePicker.value = !showPickupDatePicker.value;
    };

    function dynamicUpdatePickupDate({ value }) {
      if (value) {
        filters.value.pickup_start = selectedPickupDate.value;
        filters.value.pickup_end = selectedPickupEndDate.value;
      }
    }

    function setSelectedPickupDay(fieldValue, dateSelected) {
      if (
        !selectedPickupDate.value ||
        (selectedPickupDate.value && selectedPickupEndDate.value)
      ) {
        // set pickup start range
        selectedPickupDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
        selectedPickupEndDate.value = undefined;
      } else {
        // set pickup end range
        selectedPickupEndDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
        dynamicUpdatePickupDate({ name: fieldValue, value: dateSelected });
        showPickupDatePicker.value = false;
      }
    }

    const handleSearch = async () => {
      console.log('handle search:');

      await store.commit('loads/setLoads', []);
      await store.commit('loads/setLoadSearchFilters', filters.value);

      const params = createFilterQuery(
        filters.value,
        router.currentRoute.value.query
      );

      // redirect to load-board route
      await router.push({
        name: 'Load Board',
        query: params,
      });
    };

    const startPickupDate = computed(() => {
      return selectedPickupDate.value
        ? dayjs(selectedPickupDate.value).format('YYYY-MM-DD')
        : undefined;
    });
    const endPickupDate = computed(() => {
      return selectedPickupEndDate.value
        ? dayjs(selectedPickupEndDate.value).format('YYYY-MM-DD')
        : undefined;
    });

    const toggleLocationInputOrder = () => {
      const previousOrigin = filters.value.origin;
      const previousDestination = filters.value.destination;
      const previousOriginDeadhead = filters.value.origin_deadhead;
      const previousDestinationDeadhead = filters.value.destination_deadhead;
      const previousSelectedOrigin = filters.value.selectedOrigin;
      const previousSelectedDestination = filters.value.selectedDestination;
      const previousOriginAreaId = filters.value.origin_area_id;
      const previousDestinationAreaId = filters.value.destination_area_id;

      const updatedFilters = {
        ...filters.value,
        origin: previousDestination,
        destination: previousOrigin,
        origin_deadhead: previousDestinationDeadhead,
        destination_deadhead: previousOriginDeadhead,
        selectedOrigin: previousSelectedDestination,
        selectedDestination: previousSelectedOrigin,
        origin_area_id: previousDestinationAreaId,
        destination_area_id: previousOriginAreaId,
      };

      filters.value = updatedFilters;
      selectedOriginDeadhead.value = previousDestinationDeadhead;
      selectedDestinationDeadhead.value = previousOriginDeadhead;
    };

    return {
      innerHeight,
      filters,
      handleSearch,
      isSearchDisabled,
      setOriginFilters,
      setSelectedOrigin,
      setDestinationFilters,
      setSelectedDestination,
      selectedOriginDeadhead,
      toggleLocationInputOrder,
      selectedDestinationDeadhead,
      handleOriginDeadheadSelected,
      handleDestinationDeadheadSelected,
      initialDate,
      selectedPickupDate,
      showPickupDatePicker,
      toggleShowPickupDatePicker,
      dynamicUpdatePickupDate,
      setSelectedPickupDay,
      selectedEquipment,
      handleSelectedEquipment,
      pickupPlaceholder,
      displayEquipmentModal,
      isEquipmentModalVisible,
      startPickupDate,
      endPickupDate,
    };
  },
};
</script>

<style>
.deadhead-dropdown > div {
  @apply w-full;
}

.equipment-dropdown > div {
  @apply my-0 rounded-lg;
}

.equipment-dropdown p {
  @apply text-left text-xs;
}

.dashboard-origin-location input {
  @apply rounded-lg rounded-r-none rounded-bl-none;
}
.dashboard-origin-dropdown > div {
  @apply my-0 rounded-lg rounded-l-none rounded-br-none;
}
.dashboard-origin-dropdown > div > div:first-of-type,
.dashboard-destination-dropdown > div > div:first-of-type,
.equipment-dropdown > div > div:first-of-type {
  @apply !h-full bg-opacity-30;
}
.dashboard-origin-dropdown > div > div:nth-of-type(2),
.dashboard-destination-dropdown > div > div:nth-of-type(2) {
  @apply z-40;
}

.dashboard-origin-dropdown p,
.dashboard-destination-dropdown p {
  min-width: 48px;
}

.dashboard-destination-location input {
  @apply rounded-lg rounded-r-none rounded-tl-none;
}
.dashboard-destination-dropdown > div {
  @apply my-0 rounded-lg rounded-l-none rounded-tr-none;
}

.equipment-dropdown input {
  @apply h-12 border border-light-gray bg-opacity-30;
  font-size: 11px;
}
</style>
